import { regionCode } from '../regions.js';
import { applicationIdentifierSpec } from './applicationIdentifier.js';

export { applicationIdentifierSpec, applicationSequenceIdentifier, type SequenceApplicationIdentifierField } from './applicationIdentifier.js';

/**
 * Canonical application identity shared by browser grouping and backend family
 * queries. Keep this module and its region normalizer free of database/runtime
 * package dependencies so a frontend-only install can consume the same rule.
 * Authority identifiers take precedence over legacy number/name fallbacks;
 * tenant scoping remains the caller's responsibility.
 */
export interface ApplicationIdentitySource {
  readonly NdaNumber: string | null;
  readonly DrugName: string | null;
  readonly Region?: string | null;
  readonly DossierId?: string | null;
  readonly EuApplicationUuid?: string | null;
  readonly ControlNumber?: string | null;
}

/** F18: one authority-specific identity for grouping, history and spec inheritance. */
export function applicationIdentityParts(sub: ApplicationIdentitySource): {
  region: string;
  field: 'NdaNumber' | 'DossierId' | 'EuApplicationUuid' | 'ControlNumber' | 'DrugName';
  value: string;
  preceding: readonly string[];
} {
  const region = regionCode(sub.Region) ?? sub.Region?.trim() ?? '';
  const primary = applicationIdentifierSpec(region)?.field ?? 'NdaNumber';
  // Review F5 — Health Canada's ControlNumber identifies a regulatory ACTIVITY,
  // not a dossier (every new activity starts as REP placeholder 000000, and one
  // dossier's sequences carry different control numbers), so it can neither
  // join two unrelated drafts nor split one dossier. HC identity is DossierId
  // (`applicationIdentifierSpec`, the route's collision identity), then the
  // legacy NdaNumber, then the weak DrugName fallback below. Other regions keep
  // their existing ControlNumber legacy fallback unchanged.
  const order = [...new Set(region === 'HC'
    ? [primary, 'NdaNumber'] as const
    : [primary, 'NdaNumber', 'ControlNumber'] as const)];
  for (const [index, field] of order.entries()) {
    const value = sub[field]?.trim();
    if (value) return { region, field, value, preceding: order.slice(0, index) };
  }
  // Legacy WEAK fallback: same-name rows with no authority identifier are
  // grouped, which is a convenience, not proof they are one application.
  return { region, field: 'DrugName', value: sub.DrugName?.trim() ?? '', preceding: order };
}

export function applicationIdentityKey(sub: ApplicationIdentitySource & { readonly Id?: string }): string {
  const identity = applicationIdentityParts(sub);
  // Azure SQL's application columns use a case-insensitive collation. Match
  // its identity semantics in browser grouping, including UUID letter case.
  return JSON.stringify([identity.region, identity.field, identity.value.toLowerCase() || `unknown-${sub.Id ?? ''}`]);
}
